/**
 * SpotifyNowPlaying — track artwork + transport controls + seekbar + volume.
 *
 * Only renders when the user is connected AND Premium (gated upstream).
 */
import React from "react";
import {
  Music, Play, Pause, SkipForward, SkipBack, Loader2, Volume2, VolumeX,
} from "lucide-react";

const fmtTime = (ms) => {
  if (!ms || ms < 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

export default function SpotifyNowPlaying({
  track,
  paused,
  position,
  duration,
  volume,
  sdk,
  sdkReady,
  onSeek,
  onVolume,
}) {
  return (
    <div className="mb-3" data-testid="spotify-now-playing">
      <div className="flex items-center gap-3">
        {/* Artwork + heartbeat dot */}
        <div className="relative shrink-0">
          {track?.album?.images?.[0]?.url ? (
            <img
              src={track.album.images[0].url}
              alt=""
              className="w-16 h-16 rounded-xl object-cover shadow-lg ring-1 ring-white/10 accent-dynamic-ring"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center ring-1 ring-white/10">
              <Music className="w-5 h-5 text-white/30" />
            </div>
          )}
          {!paused && track && (
            <span
              className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full accent-dynamic-bg ring-2 ring-black animate-pulse"
              aria-hidden
            />
          )}
        </div>

        {/* Title + artist */}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate" data-testid="spotify-track-title">
            {track?.name || (sdkReady ? "Pick something below to start" : "Loading player…")}
          </div>
          <div className="text-xs text-white/50 truncate" data-testid="spotify-track-artist">
            {(track?.artists || []).map((a) => a.name).join(", ") || "—"}
          </div>
        </div>

        {/* Transport */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => sdk.prev()}
            disabled={!sdkReady}
            className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
            data-testid="spotify-prev"
            aria-label="Previous"
          >
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            onClick={() => sdk.togglePlay()}
            disabled={!sdkReady}
            className="rounded-full bg-white hover:bg-white/90 text-black w-10 h-10 inline-flex items-center justify-center disabled:opacity-30 transition-colors shadow-lg shadow-black/40"
            data-testid={paused ? "spotify-play" : "spotify-pause"}
            aria-label={paused ? "Play" : "Pause"}
          >
            {!sdkReady ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : paused ? (
              <Play className="w-4 h-4 ml-0.5" />
            ) : (
              <Pause className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => sdk.next()}
            disabled={!sdkReady}
            className="rounded-full p-2 text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors"
            data-testid="spotify-next"
            aria-label="Next"
          >
            <SkipForward className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Seek bar */}
      <div className="flex items-center gap-3 mt-3 text-[10px] text-white/40 tabular-nums">
        <span data-testid="spotify-position">{fmtTime(position)}</span>
        <input
          type="range"
          min={0}
          max={duration || 1}
          value={Math.min(position, duration || 0)}
          onChange={onSeek}
          disabled={!sdkReady || !track}
          className="player-range flex-1 disabled:opacity-30"
          data-testid="spotify-seek"
        />
        <span data-testid="spotify-duration">{fmtTime(duration)}</span>
      </div>

      {/* Volume */}
      <div className="flex items-center gap-3 mt-2 text-[10px] text-white/40">
        {volume === 0 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={onVolume}
          disabled={!sdkReady}
          className="player-range flex-1 disabled:opacity-30"
          data-testid="spotify-volume"
        />
      </div>
    </div>
  );
}
